// The lower part of a result: what the reviewer read from the file (format
// and transcript, with transcript lines that jump to their frame) and the
// history of AI checks and human decisions.
import React, { useState } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import SubtitlesOutlinedIcon from '@mui/icons-material/SubtitlesOutlined';
import { fmtDateTime } from './qcFormat';
import { asText } from './qcClient';
import { TimeChip } from './ResultChecklist';
import { C, Disclosure, SectionTitle, VerdictBadge } from './ui';

const audioText = (hasAudio) => {
    if (hasAudio === false) return 'No audio track';
    return hasAudio === true ? 'Yes' : '—';
};

const TRANSCRIPT_ID = 'vqc-transcript';

export function Details({ video, onJump }) {
    const [showTranscript, setShowTranscript] = useState(false);
    const meta = video.format_meta;
    const segments = (video.transcript?.segments || []).filter((s) => typeof s?.text === 'string');
    if (!meta && !segments.length) return null;
    const tiles = meta ? [
        { label: 'Duration', value: asText(meta.duration, '—') },
        { label: 'Resolution', value: asText(meta.resolution, '—') },
        { label: 'Aspect ratio', value: asText(meta.aspect_ratio, '—') },
        { label: 'Audio', value: audioText(meta.has_audio) },
    ] : [];
    return (
        <div>
            <SectionTitle hint="What the AI reviewer read from the file">Format{segments.length ? ' and transcript' : ''}</SectionTitle>
            {tiles.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
                    {tiles.map((tile) => (
                        <div key={tile.label} style={{ background: C.soft, border: `1px solid ${C.line}`, borderRadius: 10, padding: '9px 12px' }}>
                            <div style={{ fontSize: 12, color: C.muted }}>{tile.label}</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginTop: 2 }}>{tile.value}</div>
                        </div>
                    ))}
                </div>
            )}
            {segments.length > 0 && (
                <div style={{ marginTop: 12 }}>
                    <Disclosure open={showTranscript} controls={TRANSCRIPT_ID} onToggle={() => setShowTranscript((v) => !v)}>
                        <SubtitlesOutlinedIcon aria-hidden="true" style={{ fontSize: 17, color: C.muted }} />
                        Transcript · {segments.length} {segments.length === 1 ? 'line' : 'lines'}
                    </Disclosure>
                    {/* The wrapper carries `hidden`: the list itself is display: flex. */}
                    <div id={TRANSCRIPT_ID} hidden={!showTranscript}>
                        <div style={{ maxHeight: 240, overflowY: 'auto', background: C.soft, borderRadius: 10, padding: 12, marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {segments.map((s, i) => (
                                // Segments have no id; their order is fixed by time.
                                // eslint-disable-next-line react/no-array-index-key
                                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 12.5, color: C.body, lineHeight: 1.55 }}>
                                    <TimeChip ts={s.start} onJump={onJump} />
                                    <span>{s.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function History({ checks }) {
    if (checks.length < 2) return null;
    return (
        <div>
            <SectionTitle>History</SectionTitle>
            <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {checks.map((c) => {
                    const human = c.kind === 'human';
                    const Icon = human ? PersonOutlineRoundedIcon : AutoAwesomeIcon;
                    return (
                        <li key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderTop: `1px solid ${C.line}`, fontSize: 12.5, color: C.muted, flexWrap: 'wrap' }}>
                            <Icon aria-hidden="true" style={{ fontSize: 16, color: human ? C.navy700 : '#c79309' }} />
                            <VerdictBadge verdict={c.verdict} />
                            <span>{human ? `Decision by ${asText(c.decided_by, 'a team member')}` : 'AI check'}</span>
                            {asText(c.decision_note) && <span style={{ color: C.body }}>“{c.decision_note}”</span>}
                            <span style={{ marginLeft: 'auto' }}>{fmtDateTime(c.created_at)}</span>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}
